import { VisitOutcome } from "../models/VisitOutcome";
import { AttendanceEntry } from "../contracts/AttendanceEntry";
import { AttendanceResponse } from "../contracts/AttendanceResponse";
import type { OccurrenceAttendance, SessionVisits } from "../visits-slice";

/**
 * Wire entries → the slice's per-occurrence attendance (outcomes + one-off member ids).
 * Members with a Confirmed Occurrence seat join the one-off list without an outcome, so
 * the panel pre-fills them until a Visit is saved.
 */
export function entriesToAttendance(
  entries: AttendanceEntry[],
  seatMemberIds: string[] = []
): OccurrenceAttendance {
  const outcomes: SessionVisits = {};
  const oneOffMemberIds: string[] = [];
  const paymentTypeIds: Record<string, string> = {};
  for (const entry of entries) {
    outcomes[entry.memberId] = entry.outcome;
    if (entry.paymentTypeId) {
      paymentTypeIds[entry.memberId] = entry.paymentTypeId;
    }
    if (entry.oneOff) {
      oneOffMemberIds.push(entry.memberId);
    }
  }
  for (const memberId of seatMemberIds) {
    if (!oneOffMemberIds.includes(memberId) && outcomes[memberId] === undefined) {
      oneOffMemberIds.push(memberId);
    }
  }
  return { outcomes, oneOffMemberIds, paymentTypeIds };
}

/**
 * A whole attendance response → the slice entry, carrying the date's cancelled state. A
 * response without the flag (an older API) reads as a date that takes place.
 */
export function responseToAttendance(response: AttendanceResponse): OccurrenceAttendance {
  return {
    ...entriesToAttendance(response.entries, response.seatMemberIds),
    cancelled: response.cancelled ?? false,
    cancelledAt: response.cancelledAt ?? null,
  };
}

/** Slice map → wire entries, dropping Unmarked (only decided outcomes are persisted). */
export function outcomesToEntries(
  outcomes: SessionVisits,
  oneOffMemberIds: Set<string> = new Set()
): AttendanceEntry[] {
  return Object.entries(outcomes)
    .filter(([, outcome]) => outcome !== VisitOutcome.Unmarked)
    .map(([memberId, outcome]) => ({
      memberId,
      outcome,
      oneOff: oneOffMemberIds.has(memberId) || undefined,
    }));
}
